import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { buildFormData, buildQueryString } from 'src/lib/utilities/api/http';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { PagingArgument } from 'src/lib/utilities/api/patterns/pagination/paging.argument';
import { CurriculumMappingSectionEditArgument } from '../../organizations/teachers/curriculum-mapping/curriculum-mapping-section.model';
import {
	CurriculumCourseModel,
	CurriculumCourseOverviewResponseModel,
	CurriculumCourseSearchArgument,
	CurriculumCourseUpsertArgument,
	CurriculumCourseVersionModel,
	CurriculumVersionCreateArgument,
	CurriculumVersionEditArgument,
} from './curriculum-course-overview.model';
import {
	CurriculumSectionCreateArgument,
	CurriculumSectionOverviewModel,
	CurriculumSectionOverviewResponseModel,
	CurriculumSectionSearchArgument,
} from './curriculum-section-overview.model';
@Injectable({
	providedIn: 'root',
})
export class CurriculumCourseService {
	constructor(private httpClient: HttpClient) {}
	/************/
	/** COURSE **/
	/************/
	public searchCourses = (
		args: PagingArgument<CurriculumCourseSearchArgument>,
	): Observable<CurriculumCourseOverviewResponseModel> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/course/search`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumCourseOverviewResponseModel, r),
					{
						errorCode: '30C85095',
					},
				),
			);
	};

	public viewCourse = (id: number): Observable<CurriculumCourseModel> => {
		return this.httpClient
			.get<{ course: unknown }>(`/api/v1/curriculum/course/${id}`)
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCourseModel, r.course), {
					errorCode: '1ACA3889',
				}),
			);
	};

	// Edit Course in postman
	public reactivateCourse = (id: number): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/course/${id}?reactivate=TRUE`, {})
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'FE06B10A',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Delete Course in postman
	public deactivateCourse = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/course/${id}?delete=FALSE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '2CC32B2E',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public createCourse = (
		args: CurriculumCourseUpsertArgument,
	): Observable<CurriculumCourseModel> => {
		return this.httpClient
			.post<{ course: unknown }>(`/api/v1/curriculum/course/create`, args)
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCourseModel, r.course), {
					errorCode: '192CC0D6',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public editCourse = (
		id: number,
		args: Partial<CurriculumCourseUpsertArgument>,
	): Observable<CurriculumCourseModel> => {
		return this.httpClient
			.post<{ course: unknown }>(`/api/v1/curriculum/course/${id}`, args)
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCourseModel, r.course), {
					errorCode: '34A53675',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteCourse = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/course/${id}?delete=TRUE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '4A555226',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/**************/
	/** SECTIONS **/
	/**************/
	public viewSection = (
		id: number,
	): Observable<CurriculumSectionOverviewModel> => {
		return this.httpClient
			.get<{ section: unknown }>(`/api/v1/curriculum/section/${id}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumSectionOverviewModel, r.section),
					{
						errorCode: '95908B4E',
					},
				),
			);
	};

	public searchSections = (
		args: PagingArgument<CurriculumSectionSearchArgument>,
		simple: boolean = false,
	): Observable<CurriculumSectionOverviewResponseModel> => {
		const qs = simple ? buildQueryString({ _simple: simple }) : '';

		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/section/search${qs}`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumSectionOverviewResponseModel, r),
					{
						errorCode: '5CF381FE',
					},
				),
			);
	};

	public createSection = (
		args: CurriculumSectionCreateArgument,
	): Observable<CurriculumSectionOverviewModel> => {
		return this.httpClient
			.post<{ section: unknown }>(`/api/v1/curriculum/section/create`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumSectionOverviewModel, r.section),
					{
						errorCode: 'D69BD55F',
					},
				),
			);
	};

	public editSection = (
		id: number,
		args: CurriculumMappingSectionEditArgument,
	): Observable<CurriculumSectionOverviewModel> => {
		const bodyArgs = {};
		// Check if syllabus_file property exists in args (even if null)
		if ('syllabus_file' in args) {
			bodyArgs['syllabus_file'] = args.syllabus_file; // Can be null, File, or undefined
			delete args.syllabus_file;
		}
		bodyArgs['jsonBody'] = args;

		return this.httpClient
			.post<{
				section: unknown;
			}>(`/api/v1/curriculum/section/${id}`, buildFormData(bodyArgs))
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumSectionOverviewModel, r.section),
					{
						errorCode: '022B8543',
					},
				),
			);
	};

	// Edit Section in postman
	public reactivateSection = (id: number): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/section/${id}?reactivate=TRUE`, {})
			.pipe(
				mapResponse((r) => r, {
					errorCode: '7005CFDE',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Delete Section in postman
	public deactivateSection = (
		id: number,
		removeFromPlans: boolean = false,
	): Observable<unknown> => {
		let qs = '?delete=FALSE';
		if (removeFromPlans) {
			qs = '?remove_from_plans=TRUE';
		}
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/section/${id}${qs}`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'C831D91D',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public replaceSection = (
		remove_section: number,
		add_section: number,
		catalogs: number[] | 'all',
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/section/replace_section`, {
				remove_section,
				add_section,
				catalogs,
			})
			.pipe(
				mapResponse((r) => r, {
					errorCode: '8EF44AE8',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteSection = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/section/${id}?delete=TRUE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '9055D463',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/**************/
	/** VERSIONS **/
	/**************/
	public viewVersion = (
		id: number,
	): Observable<CurriculumCourseVersionModel> => {
		return this.httpClient
			.get<{ version: unknown }>(`/api/v1/curriculum/version/${id}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumCourseVersionModel, r.version),
					{
						errorCode: 'F0358146',
					},
				),
			);
	};

	public createVersion = (
		args: CurriculumVersionCreateArgument,
	): Observable<CurriculumCourseVersionModel> => {
		return this.httpClient
			.post<{ version: unknown }>(`/api/v1/curriculum/version/create`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumCourseVersionModel, r.version),
					{
						errorCode: '6DF060C1',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public editVersion = (
		id: number,
		args: Partial<CurriculumVersionEditArgument>,
	): Observable<CurriculumCourseVersionModel> => {
		return this.httpClient
			.post<{ version: unknown }>(`/api/v1/curriculum/version/${id}`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumCourseVersionModel, r.version),
					{
						errorCode: '6CC70CE8',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	// Edit Version in postman
	public reactivateVersion = (id: number): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/version/${id}?reactivate=TRUE`, {})
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'FE06B10A',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Delete Version in postman
	public deactivateVersion = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/version/${id}?delete=FALSE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'A52ADB3E',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteVersion = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/version/${id}?delete=TRUE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '0FB48A8C',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};
}
